import { useEffect, useId } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, FormControl } from '@mui/material';
import { useAppDispatch } from 'store/hooks';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Input from 'components/Input';
import { createRecordingBookmarkRequested } from 'features/recorder/actions';
import { recordingBookmarkSchema } from 'features/recorder/schemas';
import { useTranslation } from 'react-i18next';
import { closeModal } from 'features/modal/modalSlice';
import { Dialog } from 'components/Dialog';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';

type AddBookmarkFormValues = z.infer<typeof recordingBookmarkSchema>;

const AddBookmarkModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();
  const modalId = useId();

  const { hasPermissions } = usePermissions();
  const canManageRecording = hasPermissions(PermissionTypes.recording);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<AddBookmarkFormValues>({
    resolver: zodResolver(recordingBookmarkSchema),
    mode: 'onChange',
    defaultValues: {
      bookmark: '',
    },
  });

  const modalShouldBeClosed = !canManageRecording;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('addBookmarkModal'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const onSubmit: SubmitHandler<AddBookmarkFormValues> = (data) => {
    dispatch(
      createRecordingBookmarkRequested({
        bookmark: data.bookmark,
      })
    );
    dispatch(closeModal('addBookmarkModal'));
  };

  const handleCancel = () => {
    reset();
    dispatch(closeModal('addBookmarkModal'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('recording.add_bookmark.title')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormControl fullWidth error={!!errors?.bookmark}>
            <Controller
              name="bookmark"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <Input
                  {...field}
                  inputRef={ref}
                  placeholder={t('recording.add_bookmark.placeholder')}
                  autoFocus
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="grayscale">
            {t('recording.add_bookmark.btn_cancel_label')}
          </Button>
          <Button type="submit" disabled={!isValid}>
            {t('recording.add_bookmark.btn_ok_label')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddBookmarkModal;
